import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { HomeBoth, HomeEngineer, HomeSme } from "../../../api/types";
import { activeRoles } from "../../../app/access";
import { useCurrentMe } from "../../../app/auth";
import { Button, Card, EmptyState, Loading, Tabs } from "../../../ui";
import { EngineerHome } from "./EngineerHome";
import { SmeHome } from "./SmeHome";

type Answer = HomeEngineer | HomeSme | HomeBoth;

function isBoth(answer: Answer): answer is HomeBoth {
  return "engineer" in answer && "sme" in answer;
}

function isEngineer(answer: Answer): answer is HomeEngineer {
  return "welcome" in answer;
}

/**
 * The home. The server answers for the roles of the person: the engineer's home, the SME's home, or both when
 * the person holds both roles (then the two are tabs, the engineer's first).
 */
export function HomePage() {
  const me = useCurrentMe();
  const home = useApiQuery("home", {});
  const roles = activeRoles(me);
  const engineer = roles.includes("engineer");

  let body;
  if (home.isPending) {
    body = <Loading>Reading your home</Loading>;
  } else if (home.isError) {
    body = (
      <Card>
        <EmptyState title="Your home could not be read" action={<Button onClick={() => void home.refetch()}>Try again</Button>}>
          {refusalText(home.error)}
        </EmptyState>
      </Card>
    );
  } else if (isBoth(home.data)) {
    const both = home.data;
    body = (
      <Tabs
        label="Your two roles"
        defaultValue="engineer"
        tabs={[
          { id: "engineer", label: "As an engineer", panel: <EngineerHome home={both.engineer} canStart={engineer} /> },
          { id: "sme", label: "As an SME", count: both.sme.hand_overs.length, panel: <SmeHome home={both.sme} /> },
        ]}
      />
    );
  } else if (isEngineer(home.data)) {
    body = <EngineerHome home={home.data} canStart={engineer} />;
  } else {
    body = <SmeHome home={home.data} />;
  }

  return (
    <div className="stack">
      <h1>Home</h1>
      {body}
    </div>
  );
}
